import React from "react";
import { Edit, FileText, Trash2 } from "lucide-react";

import type { StreamLink, StreamSettingRow } from "./StreamSetting.types";
import Pagination from "../../../../utils/Pagination";

interface StreamSettingsTableProps {
  currentPage: number;
  pageSize: number;
  paginatedData: StreamSettingRow[];
  totalPages: number;
  onAdd: () => void;
  onDeleteClick: (id: string) => void;
  onEdit: (item: StreamSettingRow) => void;
  onPageChange: (page: number) => void;
  onSeeMore: (links: StreamLink[]) => void;
}

const StreamSettingsTable: React.FC<StreamSettingsTableProps> = ({
  currentPage,
  pageSize,
  paginatedData,
  totalPages,
  onAdd,
  onDeleteClick,
  onEdit,
  onPageChange,
  onSeeMore,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-xl font-semibold text-gray-800">Stream setting</h2>
        <button
          onClick={onAdd}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium w-full sm:w-auto"
        >
          + Add Stream
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="md:table w-full">
          <thead className="bg-gray-100">
            <tr className="border border-gray-100">
              <th className="text-left p-2 text-gray-800 font-medium">S. No</th>
              <th className="text-left p-2 text-gray-800 font-medium">
                Court Room
              </th>
              <th className="text-left p-2 text-gray-800 font-medium">
                Court Type
              </th>
              <th className="text-left p-2 text-gray-800 font-medium">Delay</th>
              <th className="text-left p-2 text-gray-800 font-medium">Links</th>
              <th className="text-left p-2 text-gray-800 font-medium">Keys</th>
              <th className="text-left p-2 text-gray-800 font-medium">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => (
                <tr
                  key={item._id}
                  className={`border-b border-gray-100 transition-colors ${
                    index % 2 === 0 ? "bg-white" : "bg-blue-50"
                  } hover:bg-gray-50`}
                >
                  <td className="p-2 text-gray-700 font-normal">
                    {(currentPage - 1) * pageSize + index + 1}
                  </td>
                  <td className="p-2 text-gray-700 font-normal">
                    {getCourtRoomName(item)}
                  </td>

                  <td className="p-2 text-gray-700 font-normal">
                    {item?.courtRoomModel === "CourtRoom"
                      ? "District Court"
                      : "High Court"}
                  </td>
                  <td className="p-2 text-gray-700 font-normal">
                    {item.delayInMinutes} min
                  </td>

                  <td className="p-2 text-sm text-blue-700 space-y-1">
                    <PreviewList
                      values={item.links.map((link) => link.link)}
                      links={item.links}
                      onSeeMore={onSeeMore}
                    />
                  </td>

                  <td className="px-2 py-2 text-gray-700 font-normal">
                    <PreviewList
                      values={item.links.map((link) => link.key)}
                      links={item.links}
                      onSeeMore={onSeeMore}
                    />
                  </td>

                  <td className="p-2 flex justify-start gap-2">
                    <button
                      onClick={() => onEdit(item)}
                      className="text-blue-500 hover:text-gray-800 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => onDeleteClick(item._id)}
                      className="p-2 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-16 text-center text-sm text-gray-500"
                >
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <FileText className="h-12 w-12 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-600 mb-0">
                      No Stream Settings Found
                    </h3>
                    <p className="text-xs text-gray-400">
                      Try adjusting your filters to see more results
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="px-6 py-4 bg-white">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface PreviewListProps {
  links: StreamLink[];
  values: string[];
  onSeeMore: (links: StreamLink[]) => void;
}

const PreviewList: React.FC<PreviewListProps> = ({
  links,
  values,
  onSeeMore,
}) => (
  <>
    {values.slice(0, 2).map((value, index) => (
      <div key={index} className="truncate max-w-xs">
        {value}
      </div>
    ))}

    {links.length > 2 && (
      <button
        className="text-blue-600 hover:text-blue-700 text-xs font-medium underline mt-1"
        onClick={() => onSeeMore(links)}
      >
        See More
      </button>
    )}
  </>
);

const getCourtRoomName = (item: StreamSettingRow) =>
  item.courtRoom ? `${item.courtRoom.number} - ${item.courtRoom.name}` : "N/A";

export default StreamSettingsTable;
